<template>
  <div class="payment-view">
    <AppHeader />
    
    <div class="payment-container">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="spinner-lg"></div>
        <p>{{ $t('preparingPayment') }}</p>
      </div>

      <!-- Error State -->
      <div v-if="error && !isLoading" class="error-state">
        <div class="error-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
        </div>
        <h2>{{ $t('paymentError') }}</h2>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button @click="retryPayment" class="btn-primary">{{ $t('tryAgain') }}</button>
          <button @click="goBackToCheckout" class="btn-secondary">{{ $t('backToCheckout') }}</button>
        </div>
      </div>

      <!-- Dintero Checkout Container - always rendered, hidden when loading/error -->
      <div 
        id="dintero-checkout-container" 
        class="checkout-embed"
        :style="{ display: (isLoading || error) ? 'none' : 'block' }"
      ></div>
    </div>

    <!-- Test Data Helper Widget -->
    <DinteroTestHelper />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useTableStore } from '@/stores/table'
import { useSessionStore } from '@/stores/session'
import { embed } from '@dintero/checkout-web-sdk'
import AppHeader from '@/components/AppHeader.vue'
import DinteroTestHelper from '@/components/DinteroTestHelper.vue'

const router = useRouter()
const tableStore = useTableStore()
const sessionStore = useSessionStore()

const isLoading = ref(true)
const error = ref(null)
let checkoutInstance = null

// Firebase Cloud Functions URL
const FUNCTIONS_BASE_URL = process.env.VUE_APP_FUNCTIONS_URL || 
  'https://europe-west1-dandero-3dff1.cloudfunctions.net'

async function initializePayment() {
  isLoading.value = true
  error.value = null

  const slug = sessionStore.restaurantSlug || 'demo'

  console.log('initializePayment called')
  console.log('sessionStore.sessionId:', sessionStore.sessionId)
  console.log('tableStore.cartItems:', tableStore.cartItems)
  console.log('tableStore.cart:', tableStore.cart)

  try {
    // Validate we have items to pay for
    if (tableStore.cartItems.length === 0) {
      console.log('Cart is empty, redirecting back')
      error.value = 'No items in cart'
      router.push(`/${slug}/checkout`)
      return
    }

    // Validate session
    if (!sessionStore.sessionId) {
      error.value = 'No active session'
      router.push(`/${slug}`)
      return
    }

    // Create Dintero payment session via Cloud Function
    const response = await fetch(`${FUNCTIONS_BASE_URL}/createDinteroSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sessionId: sessionStore.sessionId,
        tableId: sessionStore.tableId
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to create payment session')
    }

    const { sid: dinteroSessionId } = await response.json()

    // Store Dintero session ID for later reference
    tableStore.setPaymentSession(dinteroSessionId)

    // Embed Dintero checkout
    checkoutInstance = await embed({
      container: document.getElementById('dintero-checkout-container'),
      sid: dinteroSessionId,
      language: sessionStore.language || 'no',
      onSession: () => {
        // Session initialized
      },
      onPayment: (event) => {
        console.log('Payment completed:', event)
        handlePaymentComplete(event)
      },
      onPaymentError: (event) => {
        console.error('Payment error:', event)
        error.value = event.error?.message || 'Payment failed'
      },
      onSessionCancel: () => {
        console.log('Payment cancelled')
        const slug = sessionStore.restaurantSlug || 'demo'
        router.push(`/${slug}/checkout`)
      },
      onSessionLocked: () => {
        console.log('Session locked - payment processing')
      },
      onActivePaymentType: (event) => {
        console.log('Active payment type:', event)
      }
    })

    isLoading.value = false
  } catch (err) {
    console.error('Payment initialization error:', err)
    error.value = err.message || 'Failed to initialize payment'
    isLoading.value = false
  }
}

function handlePaymentComplete(event) {
  // Navigate to completion page with payment result
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push({
    path: `/${slug}/payment/complete`,
    query: {
      merchant_reference: event.merchant_reference,
      transaction_id: event.transaction_id,
      status: 'success'
    }
  })
}

function retryPayment() {
  error.value = null
  initializePayment()
}

function goBackToCheckout() {
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/checkout`)
}

onMounted(async () => {
  // Bind to Firebase session to get cart data
  if (sessionStore.sessionId) {
    tableStore.bindSession(sessionStore.sessionId)
    // Wait a moment for Firebase to sync
    await new Promise(resolve => setTimeout(resolve, 500))
  }
  initializePayment()
})

onUnmounted(() => {
  // Cleanup Dintero checkout instance
  if (checkoutInstance && typeof checkoutInstance.destroy === 'function') {
    checkoutInstance.destroy()
  }
  // Unbind from Firebase
  tableStore.unbindTable()
})
</script>

<style scoped>
.payment-view {
  min-height: 100vh;
  background: var(--bg-primary);
}

.payment-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 16px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
}

.spinner-lg {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  color: var(--text-secondary);
  font-size: 1rem;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 24px;
}

.error-icon {
  width: 64px;
  height: 64px;
  background: #fee2e2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.error-icon svg {
  width: 32px;
  height: 32px;
  stroke: #dc2626;
}

.error-state h2 {
  font-size: 1.25rem;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.error-state p {
  color: var(--text-secondary);
  margin-bottom: 24px;
}

.error-actions {
  display: flex;
  gap: 12px;
}

/* Checkout Embed Container */
.checkout-embed {
  min-height: 600px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Button Styles */
.btn-primary {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: var(--primary-hover);
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

/* Mobile Responsiveness */
@media (max-width: 480px) {
  .payment-container {
    padding: 16px 12px;
  }

  .error-actions {
    flex-direction: column;
    width: 100%;
  }

  .error-actions button {
    width: 100%;
  }

  .checkout-embed {
    min-height: 500px;
    border-radius: 8px;
  }
}
</style>
